import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {setError, setToast} from "@arboxappv4/shared/src/redux/siteSlice";
import {t} from "i18next";
import {notification} from "antd";
import {updateError, updateToastMessage} from "@arboxappv4/shared/src/redux/stepperSlice";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {light} from "@fortawesome/fontawesome-svg-core/import.macro";
import {Btn, FlexRowSection, SmallHeader} from "../../styles/globalStyles";
import {useNavigate} from "react-router-dom";
import {isRTL} from "../i18n";
import {onLinkClicked} from "../functions";
import {stepOptions} from "../../flowManager/StepOptions";
import {flowOptions} from "../../flowManager/FlowOptions";
import dayjs from "dayjs";
import {DB_DATE_FORMAT} from "@arboxappv4/shared/src/constants/constants";
import orderBy from "lodash-es/orderBy";
import {projectTypeMainSlice} from "@arboxappv4/shared/src/helpers/constants";
import {setKioskError} from "@arboxappv4/shared/src/redux/kioskStandAlone/kioskSlice";
import {openModal, REDIRECT_LOADER_MODAL} from "@arboxappv4/shared/src/redux/modalManagerSlice";

const useCustomMessage = () => {
    const project_type = useSelector(state => state.project?.project_type)
    const siteError = useSelector(state => state.site?.error)
    const stepperError = useSelector(state => state.stepper.error)
    const kioskError = useSelector(state => state.kiosk?.error)
    const flowRes = useSelector(state => state.stepper.flowRes)
    const box = useSelector(state => state[projectTypeMainSlice[project_type]]?.box)
    const currStep = useSelector(state => state.stepper.currStep)
    const flowName = useSelector(state => state.stepper.flowName)
    const loggedIn = useSelector(state => state.auth?.loggedIn)
    const memberships = useSelector(state => state.user?.memberships)
    const language = useSelector(state => state[projectTypeMainSlice[project_type]]?.language)
    const siteToast = useSelector(state => state.site?.toast)
    const toastMessage = useSelector(state => state.stepper.toastMessage)
    const [messageApi, contextHolder] = notification.useNotification({maxCount: 2});
    const [muNotified, setMuNotified] = useState(false)
    const dispatch = useDispatch()
    const navigate = useNavigate()

    useEffect(() => {
        if(siteError) {
            handleError(siteError)
            dispatch(setError(null))
        }
    }, [siteError]);

    useEffect(() => {
        if(stepperError) {
            setTimeout(() => handleBookingWaiverError(), 500)
        }
    }, [stepperError]);

    useEffect(() => {
        if(!muNotified && loggedIn && memberships && [flowOptions.MEMBERSHIP_PURCHASE, flowOptions.DIRECT_MEMBERSHIP_PURCHASE].includes(flowName) && currStep === stepOptions.PAYMENT) {
            handleActiveMembershipNotification()
        }
    }, [currStep, loggedIn, memberships, flowName, muNotified]);


    useEffect(() => {
        if(flowRes?.showSuccessToast) {
            messageApi.success({
                message: flowRes.showSuccessToast.title ?? t('success'),
                description: flowRes.showSuccessToast.content ?? '',
                closeIcon: (<FontAwesomeIcon icon={light('xmark')}/>),
                className: `app-notification ${isRTL() ? 'notification-rtl' : ''}`,
                placement: isRTL() ? 'topLeft' : 'topRight',
            });
        }
    }, [flowRes]);

    useEffect(() => {
        if(toastMessage) {
            messageApi[toastMessage.type ?? 'success']({
                message: toastMessage.title ?? t('success'),
                description: toastMessage.content ?? '',
                closeIcon: (<FontAwesomeIcon icon={light('xmark')}/>),
                className: `app-notification ${isRTL() ? 'notification-rtl' : ''}`,
                placement: isRTL() ? 'topLeft' : 'topRight',
                duration: 4,
            });
            dispatch(updateToastMessage(null))
        }
    }, [toastMessage]);

    useEffect(() => {
        if(siteToast) {
            messageApi[siteToast.type ?? 'success']({
                message: siteToast.title ?? t('success'),
                description: siteToast.content ?? '',
                closeIcon: (<FontAwesomeIcon icon={light('xmark')}/>),
                className: `app-notification ${isRTL() ? 'notification-rtl' : ''}`,
                placement: isRTL() ? 'topLeft' : 'topRight',
                duration: 4,
            });
            dispatch(setToast(null))
        }
    }, [siteToast]);

    useEffect(() => {
        if(kioskError) {
            handleKioskError(kioskError)
            dispatch(setKioskError(null))
        }
    }, [kioskError]);

    const handleError = (error) => {
        if(error === 'no-viewable-locations-in-box') {
            onLinkClicked('/404', navigate, {state: {notFoundHeader: t('no-viewable-locations-error-header'), notFoundSubHeader: t('no-viewable-locations-error')}})
            return
        }
        const errorText = Array.isArray(error) ? (error.every(i => typeof i === "string") ? error.map(i => `\u2022 ${i}`).join('\n') : error.map(i => `\u2022 ${i.message}`).join('\n')) : error
        messageApi.error({
            message: t('error'),
            description: errorText,
            duration: 0,
            closeIcon: (<FontAwesomeIcon icon={light('xmark')}/>),
            className: `app-notification ${isRTL() ? 'notification-rtl' : ''}`,
            placement: isRTL() ? 'topLeft' : 'topRight',
        });
    }

    const handleKioskError = (error) => {
        const header = error === 'no-token-verification-option-error' ? t('no-token-verification-option-header') : t('error')
        const errorMessage = error === 'no-token-verification-option-error' ? t(error) : error
        messageApi.error({
            message: header,
            description: errorMessage,
            duration: 0,
            closeIcon: (<FontAwesomeIcon icon={light('xmark')}/>),
            className: `app-notification ${isRTL() ? 'notification-rtl' : ''}`,
            placement: isRTL() ? 'topLeft' : 'topRight',
        });
    }

    const handleBookingWaiverError = () => {
        if(Array.isArray(stepperError) && stepperError.some(err => err.name === 'medicalWavierRestricted') && box.digital_form_id) {
            messageApi.warning({
                message: t('header-waiver-notification'),
                description: t('booking-waiver-notification'),
                duration: 0,
                placement: isRTL() ? 'topLeft' : 'topRight',
                closeIcon: (<FontAwesomeIcon icon={light('xmark')}/>),
                btn: <Btn type={'primary'} onClick={() => {
                    onLinkClicked('/form', navigate)
                    messageApi.destroy()
                }}>{t('fill-form')}</Btn>,
                className: `app-notification ${isRTL() ? 'notification-rtl' : ''}`
            });
        } else {
            handleError(stepperError)
        }
        dispatch(updateError(null))
    }

    const handleActiveMembershipNotification = () => {
        const activeOrFutureMu = memberships.filter(mt => mt.active > 0)
        if(activeOrFutureMu.length) {
            setMuNotified(true)
            messageApi.open({
                message: <SmallHeader fontSize={'18px'}>{t('hold-on')}</SmallHeader>,
                description: getMembershipsDescription(activeOrFutureMu),
                duration: 0,
                closeIcon: (<FontAwesomeIcon icon={light('xmark')}/>),
                className: `app-notification ${isRTL() ? 'notification-rtl' : ''}`,
                placement: isRTL() ? 'topLeft' : 'topRight',
                btn: (
                        <FlexRowSection justify={'end'} gap={'10px'}>
                            <Btn onClick={() => messageApi.destroy()}>{t('close')}</Btn>
                            <Btn type={'primary'} onClick={() => window.location.reload()}>{t('back-to-shop')}</Btn>
                        </FlexRowSection>
                    )
            });
        }
    }

    const getMembershipsDescription = (activeOrFutureMu) => {
        let infoText = ''
        const sorted = orderBy(activeOrFutureMu, ['start']);
        const membershipsText = sorted.reduce((acc, mu) => {
            const text = getMembershipText(mu)
            return [...acc, text]
        }, [])

        if(activeOrFutureMu.length === 1) {
            infoText += `${t('header-reminder-membership')} ${membershipsText[0]}`
        } else {
            infoText += `${t('header-reminder-membership-multi')}:\n`
            infoText += membershipsText.map((message) => `\u2022 ${message}`).join('\n')
        }

        return `${infoText}\n${t('friendly-reminder-membership')}`
    }

    const getMembershipText = (mu) => {
        let text = t('mu-called', {name: mu.membership_types.name, interpolation: {'escapeValue': false}})
        const futureStart = mu.active === 2 ? dayjs(mu.start, DB_DATE_FORMAT).locale(language?.code ?? 'en').format('MMM DD, YYYY') : null
        const expiration = mu.end ? dayjs(mu.end, DB_DATE_FORMAT).locale(language?.code ?? 'en').format('MMM DD, YYYY') : null
        const sessionsLeft = mu.sessions_left ? t('left-sessions', {num: mu.sessions_left}) : null
        if(futureStart || expiration || sessionsLeft) {
            text += ` (`
            if(futureStart) {
                text += t('begins-mu', {start: futureStart})
            }
            if(expiration) {
                text += t('expires-mu', {end: expiration})
            }
            if(sessionsLeft) {
                if(futureStart || expiration){
                    text += `, ${sessionsLeft}`
                } else {
                    text += sessionsLeft
                }
            }
            text += `).`
        }
        return text
    }

    return contextHolder

};

export default useCustomMessage;